import React, { useState, useEffect } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import axios from 'axios';
import { PencilSquare, Trash, Plus } from 'react-bootstrap-icons';
import PublicSchedule from './PublicSchedule';

function initials(name) {
  if (!name) return '?';
  return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
}

// ─── Teacher table ──────────────────────────────────────────────────────────
function TeacherTable({ teachers, onEdit, onDelete }) {
  if (teachers.length === 0) {
    return (
      <div style={{ padding: '48px 0', textAlign: 'center', color: 'var(--ink-3)', fontSize: 14 }}>
        Noch keine Einträge vorhanden.
      </div>
    );
  }
  return (
    <div className="card-flat" style={{ overflow: 'hidden' }}>
      {/* header row */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '40px 1fr 1fr 120px 72px',
        padding: '10px 18px',
        background: 'var(--surface)',
        borderBottom: '1px solid var(--line)',
        gap: 12,
      }}>
        <div />
        {['Name', 'Benutzername', 'Rolle', ''].map((h, i) => (
          <div key={i} className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            {h}
          </div>
        ))}
      </div>
      {/* data rows */}
      {teachers.map((u, i) => (
        <div key={u.id} style={{
          display: 'grid',
          gridTemplateColumns: '40px 1fr 1fr 120px 72px',
          padding: '12px 18px', alignItems: 'center', gap: 12,
          borderBottom: i < teachers.length - 1 ? '1px solid var(--line-soft)' : 'none',
        }}>
          <div className="avatar">{initials(u.name)}</div>
          <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink-1)' }}>{u.name}</div>
          <div className="mono" style={{ fontSize: 12, color: 'var(--ink-2)' }}>{u.username}</div>
          <div className="mono" style={{ fontSize: 12, color: 'var(--ink-2)' }}>Lehrer</div>
          <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
            <button className="btn-icon" onClick={() => onEdit(u)} title="Bearbeiten"><PencilSquare size={14} /></button>
            <button className="btn-icon" onClick={() => onDelete(u)} title="Löschen"><Trash size={14} /></button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Dashboard ────────────────────────────────────────────────────────────────
function AdminDashboard() {
  const [teachers,       setTeachers]       = useState([]);
  const [activeTab,      setActiveTab]      = useState('uebersicht');
  const [showModal,      setShowModal]      = useState(false);
  const [editingTeacher, setEditingTeacher] = useState(null);
  const [form,           setForm]           = useState({ name: '', username: '', password: '' });
  const [error,          setError]          = useState('');
  const [subjects,       setSubjects]       = useState([]);
  const [showSubjModal,  setShowSubjModal]  = useState(false);
  const [editingSubject, setEditingSubject] = useState(null);
  const [subjForm,       setSubjForm]       = useState({ id: '', name: '', short: '' });
  const [classes,        setClasses]        = useState([]);
  const [showClassModal, setShowClassModal] = useState(false);
  const [editingClass,   setEditingClass]   = useState(null);
  const [classForm,      setClassForm]      = useState({ name: '', teacherIds: [] });

  const token   = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchTeachers(); fetchSubjects(); fetchClasses(); }, []);

  const fetchTeachers = async () => {
    try { const r = await axios.get('/api/admin/teachers', { headers }); setTeachers(r.data); }
    catch (e) { console.error(e); }
  };
  const fetchSubjects = async () => {
    try { const r = await axios.get('/api/admin/subjects', { headers }); setSubjects(r.data); }
    catch (e) { console.error(e); }
  };
  const fetchClasses = async () => {
    try { const r = await axios.get('/api/admin/classes', { headers }); setClasses([...r.data].sort((a, b) => a.name.localeCompare(b.name, 'de'))); }
    catch (e) { console.error(e); }
  };

  const openCreateTeacher = () => {
    setEditingTeacher(null);
    setForm({ name: '', username: '', password: '' });
    setError(''); setShowModal(true);
  };
  const openEditTeacher = (teacher) => {
    setEditingTeacher(teacher);
    setForm({ name: teacher.name, username: teacher.username, password: '' });
    setError(''); setShowModal(true);
  };
  const handleSaveTeacher = async () => {
    setError('');
    try {
      if (editingTeacher) await axios.put(`/api/admin/teachers/${editingTeacher.id}`, form, { headers });
      else                await axios.post('/api/admin/teachers', form, { headers });
      setShowModal(false);
      fetchTeachers();
    } catch (e) { setError(e.response?.data?.message || 'Fehler beim Speichern'); }
  };
  const handleDeleteTeacher = async (teacher) => {
    if (!window.confirm(`Lehrer "${teacher.name}" wirklich löschen?`)) return;
    try {
      await axios.delete(`/api/admin/teachers/${teacher.id}`, { headers });
      fetchTeachers();
    } catch (e) { console.error(e); }
  };

  const openCreateSubject = () => {
    setEditingSubject(null);
    setSubjForm({ id: '', name: '', short: '' });
    setShowSubjModal(true);
  };
  const openEditSubject = (subj) => {
    setEditingSubject(subj);
    setSubjForm({ id: subj.id, name: subj.name, short: subj.short || '' });
    setShowSubjModal(true);
  };
  const handleSaveSubject = async () => {
    try {
      if (editingSubject) {
        await axios.put(`/api/admin/subjects/${editingSubject.id}`, { name: subjForm.name, short: subjForm.short }, { headers });
      } else {
        await axios.post('/api/admin/subjects', subjForm, { headers });
      }
      setShowSubjModal(false);
      fetchSubjects();
    } catch (e) { console.error(e); }
  };
  const handleDeleteSubject = async (id) => {
    if (!window.confirm('Fach wirklich löschen?')) return;
    try { await axios.delete(`/api/admin/subjects/${id}`, { headers }); fetchSubjects(); }
    catch (e) { console.error(e); }
  };

  const openCreateClass = () => {
    setEditingClass(null);
    setClassForm({ name: '', teacherIds: [] });
    setShowClassModal(true);
  };
  const openEditClass = (cls) => {
    setEditingClass(cls);
    setClassForm({ name: cls.name, teacherIds: cls.teacherIds || [] });
    setShowClassModal(true);
  };
  const toggleClassTeacher = (id) => {
    setClassForm(f => ({
      ...f,
      teacherIds: f.teacherIds.includes(id) ? f.teacherIds.filter(x => x !== id) : [...f.teacherIds, id],
    }));
  };
  const handleSaveClass = async () => {
    if (!classForm.name.trim()) return;
    try {
      let classId = editingClass?.id;
      if (editingClass) {
        await axios.put(`/api/admin/classes/${classId}`, { name: classForm.name }, { headers });
      } else {
        const r = await axios.post('/api/admin/classes', { name: classForm.name }, { headers });
        classId = r.data.id;
      }
      await axios.put(`/api/admin/classes/${classId}/teachers`, { teacherIds: classForm.teacherIds }, { headers });
      setShowClassModal(false);
      fetchClasses();
    } catch (e) { console.error(e); }
  };
  const handleDeleteClass = async (cls) => {
    if (!window.confirm(`Klasse "${cls.name}" wirklich löschen?`)) return;
    try { await axios.delete(`/api/admin/classes/${cls.id}`, { headers }); fetchClasses(); }
    catch (e) { alert(e.response?.data?.message || 'Fehler beim Löschen'); }
  };

  return (
    <div style={{ background: 'var(--bg)', minHeight: 'calc(100vh - 57px)', padding: '32px 40px 60px' }}>

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 28 }}>
        <div>
          <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>
            Verwaltung
          </div>
          <h1 className="display" style={{ fontSize: 32, margin: 0 }}>Lehrer &amp; Klassen</h1>
        </div>
        {activeTab !== 'uebersicht' && (
          <button
            className="btn btn-grad"
            onClick={() => activeTab === 'subjects' ? openCreateSubject() : activeTab === 'classes' ? openCreateClass() : openCreateTeacher()}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <Plus size={16} />
            {activeTab === 'subjects' ? 'Neues Fach' : activeTab === 'classes' ? 'Neue Klasse' : 'Neuer Lehrer'}
          </button>
        )}
      </header>

      {/* ── Tab row ────────────────────────────────────────────────────── */}
      <div className="tab-row" style={{ marginBottom: 24 }}>
        <button className={activeTab === 'uebersicht' ? 'active' : ''} onClick={() => setActiveTab('uebersicht')}>Übersicht</button>
        <button className={activeTab === 'teachers'   ? 'active' : ''} onClick={() => setActiveTab('teachers')}>Lehrer · {teachers.length}</button>
        <button className={activeTab === 'classes'    ? 'active' : ''} onClick={() => setActiveTab('classes')}>Klassen · {classes.length}</button>
        <button className={activeTab === 'subjects'   ? 'active' : ''} onClick={() => setActiveTab('subjects')}>Fächer</button>
      </div>

      <div>
        {activeTab === 'uebersicht' && (
          <div style={{ margin: '0 -40px' }}>
            <PublicSchedule />
          </div>
        )}

        {activeTab === 'teachers' && (
          <TeacherTable
            teachers={teachers}
            onEdit={openEditTeacher}
            onDelete={handleDeleteTeacher}
          />
        )}

        {activeTab === 'classes' && (
          <div>
            {classes.length === 0
              ? <div style={{ padding: '48px 0', textAlign: 'center', color: 'var(--ink-3)', fontSize: 14 }}>Keine Klassen vorhanden.</div>
              : (
                <div className="card-flat" style={{ overflow: 'hidden' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 72px', padding: '10px 18px', background: 'var(--surface)', borderBottom: '1px solid var(--line)', gap: 12 }}>
                    {['Name', 'Lehrer', ''].map((h, i) => (
                      <div key={i} className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{h}</div>
                    ))}
                  </div>
                  {classes.map((cls, i) => (
                    <div key={cls.id} style={{
                      display: 'grid', gridTemplateColumns: '1fr 1fr 72px',
                      padding: '12px 18px', alignItems: 'center', gap: 12,
                      borderBottom: i < classes.length - 1 ? '1px solid var(--line-soft)' : 'none',
                    }}>
                      <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink-1)' }}>{cls.name}</div>
                      <div className="mono" style={{ fontSize: 12, color: 'var(--ink-2)' }}>
                        {(cls.teacherIds || []).map(id => teachers.find(t => t.id === id)?.name).filter(Boolean).join(', ') || '—'}
                      </div>
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                        <button className="btn-icon" onClick={() => openEditClass(cls)} title="Bearbeiten"><PencilSquare size={14} /></button>
                        <button className="btn-icon" onClick={() => handleDeleteClass(cls)} title="Löschen"><Trash size={14} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )
            }
          </div>
        )}

        {activeTab === 'subjects' && (
          <div>
            {subjects.length === 0
              ? <div style={{ padding: '48px 0', textAlign: 'center', color: 'var(--ink-3)', fontSize: 14 }}>Keine Fächer vorhanden.</div>
              : (
                <div className="card-flat" style={{ overflow: 'hidden' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '60px 1fr 80px 72px', padding: '10px 18px', background: 'var(--surface)', borderBottom: '1px solid var(--line)', gap: 12 }}>
                    {['ID', 'Name', 'Kürzel', ''].map((h, i) => (
                      <div key={i} className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{h}</div>
                    ))}
                  </div>
                  {subjects.map((subj, i) => (
                    <div key={subj.id} style={{
                      display: 'grid', gridTemplateColumns: '60px 1fr 80px 72px',
                      padding: '12px 18px', alignItems: 'center', gap: 12,
                      borderBottom: i < subjects.length - 1 ? '1px solid var(--line-soft)' : 'none',
                    }}>
                      <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>{subj.id}</div>
                      <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink-1)' }}>{subj.name}</div>
                      <div className="mono" style={{ fontSize: 12, color: 'var(--ink-2)' }}>{subj.short || '—'}</div>
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                        <button className="btn-icon" onClick={() => openEditSubject(subj)} title="Bearbeiten"><PencilSquare size={14} /></button>
                        <button className="btn-icon" onClick={() => handleDeleteSubject(subj.id)} title="Löschen"><Trash size={14} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )
            }
          </div>
        )}
      </div>

      {/* ── Modal (Bootstrap, token-styled via swiss.css) ──────────────── */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editingTeacher ? 'Lehrer bearbeiten' : 'Lehrer erstellen'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <p style={{ fontSize: 13, color: 'var(--danger)', marginBottom: 12 }}>{error}</p>}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control type="text" value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="Vor- und Nachname" />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Benutzername</Form.Label>
              <Form.Control type="text" value={form.username}
                onChange={e => setForm({ ...form, username: e.target.value })}
                placeholder="Benutzername" />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Passwort</Form.Label>
              <Form.Control type="password" value={form.password}
                onChange={e => setForm({ ...form, password: e.target.value })}
                placeholder={editingTeacher ? 'Leer lassen um nicht zu ändern' : 'Passwort eingeben'} />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Abbrechen</Button>
          <Button variant="primary" onClick={handleSaveTeacher}>Speichern</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showSubjModal} onHide={() => setShowSubjModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editingSubject ? 'Fach bearbeiten' : 'Neues Fach'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>ID (z.B. "math", "sport")</Form.Label>
              <Form.Control type="text" value={subjForm.id}
                onChange={e => setSubjForm(p => ({ ...p, id: e.target.value }))}
                disabled={!!editingSubject} placeholder="math" />
              {!editingSubject && <Form.Text className="text-muted">Kann nach dem Erstellen nicht geändert werden.</Form.Text>}
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control type="text" value={subjForm.name}
                onChange={e => setSubjForm(p => ({ ...p, name: e.target.value }))}
                placeholder="Mathematik" />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Kürzel (2–4 Zeichen)</Form.Label>
              <Form.Control type="text" value={subjForm.short}
                onChange={e => setSubjForm(p => ({ ...p, short: e.target.value }))}
                placeholder="MA" maxLength={4} />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSubjModal(false)}>Abbrechen</Button>
          <Button variant="primary" onClick={handleSaveSubject}>Speichern</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showClassModal} onHide={() => setShowClassModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editingClass ? 'Klasse bearbeiten' : 'Neue Klasse'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control type="text" value={classForm.name}
                onChange={e => setClassForm(f => ({ ...f, name: e.target.value }))}
                placeholder="z.B. 7d" />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Lehrer</Form.Label>
              <div style={{ maxHeight: 140, overflowY: 'auto', border: '1px solid var(--line)', borderRadius: 'var(--r-s)', padding: 8 }}>
                {teachers.length === 0
                  ? <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>Keine Lehrer vorhanden.</div>
                  : teachers.map(t => (
                    <Form.Check key={t.id} type="checkbox" label={t.name}
                      checked={classForm.teacherIds.includes(t.id)}
                      onChange={() => toggleClassTeacher(t.id)} />
                  ))}
              </div>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowClassModal(false)}>Abbrechen</Button>
          <Button variant="primary" onClick={handleSaveClass}>Speichern</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default AdminDashboard;
